import { PlusOutlined } from "@ant-design/icons";
import { Button, Form, Select } from "antd";
import AuthorisedContainer from "components/AuthorisedContainer";
import BaseModalForm from "components/BaseModalForm";
import DeleteButton from "components/DeleteButton";
import TableContainer from "components/TableContainer";
import { PermissionSetMap } from "constants/RBACConstants";
import _ from "lodash";
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import GlobalSettingAPI from "restAPI/GlobalSettingAPI";
import { FieldTypes } from "utils/formUtils";
import ModalUtil from "utils/modalUtil";
import { Sorters } from "utils/tableUtils";
import { TIER1, TIER_OPTIONS, TIER_OPTIONS_WITH_CUSTOM, getTagKey, getTagKeyMap } from "utils/tierUtil";

const TierBasedTags = forwardRef(function TierBasedTags({ allTags }, ref) {
  const [selectedTier, setSelectedTier] = useState(TIER1);
  const [tierBasedTags, setTierBasedTags] = useState({});

  useImperativeHandle(ref, () => ({
    syncTags: async (_tags) => {
      const tierData = {};
      const deleted = {};
      const added = {};
      const allTagsMap = getTagKeyMap(_tags || allTags);
      _.forEach(TIER_OPTIONS, ({value: tier}) => {
        const existingTagKeys = _.map(tierBasedTags[tier], getTagKey);
        const updatedTags = _.filter(tierBasedTags[tier], tagObj => allTagsMap[getTagKey(tagObj)]);
        const updatedTagKeys = _.map(updatedTags, getTagKey);
        const addedTags = _.difference(updatedTagKeys, existingTagKeys);
        const removedTags = _.difference(existingTagKeys, updatedTagKeys);
        deleted[tier] = removedTags;
        added[tier] = addedTags;
        
        tierData[tier] = _.map(updatedTags, tagObj => ({ ...tagObj, ...allTagsMap[getTagKey(tagObj)] }));
      });
      const payload = {
        tierData,
        deleted,
        new: added
      }
      await GlobalSettingAPI.updateTierBasedTags(payload).promise.then(() => {
        setTierBasedTags(tierData);
      });
    }
  }), [allTags, tierBasedTags]);


  const onDelete = useCallback((note, deletedTag) => {
    const key = getTagKey(deletedTag);
    const tierData = _.cloneDeep(tierBasedTags);
    tierData[selectedTier] = _.filter(tierData[selectedTier], tagObj => getTagKey(tagObj) !== key);
    const payload = {
      tierData,
      deleted: {
        [selectedTier]: [key]
      }
    }
    return GlobalSettingAPI.updateTierBasedTags(payload).promise.then(() =>
      setTierBasedTags(tierData)
    );
  }, [selectedTier, tierBasedTags]);

  useEffect(() => {
    const { cancel, promise } = GlobalSettingAPI.getTierBasedTags();
    promise.then((response) => {
      if(!_.isEmpty(response)){
        setTierBasedTags(response);
      }
    })
    return cancel;
  }, []);

  const columns = useMemo(() => [
    {
      title: "Key",
      dataIndex: "name",
      width: "35%",
      render: (value, currentRecord) => {
        return value;
      },
      sorter: Sorters.STRING("name"),
    },
    {
      title: "Value",
      dataIndex: "fieldValue",
      width: "21%",
      render: (value, currentRecord) => {
        return value;
      },
      sorter: Sorters.STRING("fieldValue"),
    },
    {
      title: "Weighting",
      dataIndex: "value",
      width: "21%",
      render: (value, currentRecord) => {
        return value;
      },
    },
    {
      title: "Actions",
      align: "center",
      dataIndex: "actions",
      width: "18%",
      render: (value, currentRecord) => {
        const permission = PermissionSetMap.UPDATE_GLOBAL_SETTING;

        return (
          <>
            <AuthorisedContainer permission={permission} tooltip="Delete Tag">
              <DeleteButton type="icon" confirmText={"Delete"} onDelete={onDelete} data={currentRecord} />
            </AuthorisedContainer>
          </>
        );
      },
    },
  ], [onDelete]);

  const saveTags = useCallback((selectedTags) => {
    const _selectedTags = _.compact(selectedTags);
    const selectedTagKeys = _.map(_selectedTags, getTagKey);
    const existingTagKeys = _.map(tierBasedTags?.[selectedTier], getTagKey);
    const deletedTags = _.difference(existingTagKeys, selectedTagKeys);
    const addedTags = _.difference(selectedTagKeys, existingTagKeys);
    const tierData = _.cloneDeep(_.omit(tierBasedTags, selectedTier));
    tierData[selectedTier] = _selectedTags;
    const payload = {
      tierData,
      new: {
        [selectedTier]: addedTags,
      },
      deleted: {
        [selectedTier]: deletedTags,
      },
    };
    return GlobalSettingAPI.updateTierBasedTags(payload).promise.then(() =>
      setTierBasedTags(tierData)
    );
  }, [selectedTier, tierBasedTags]);

  const addTagsClick = () => {
    ModalUtil.show({
      content: <AddTagsToTierPopup tier={selectedTier} allTags={allTags} saveTags={saveTags} tierBasedTags={tierBasedTags} tags={tierBasedTags?.[selectedTier] ?? []} />,
    });
  }
  const title = (
    <div className="m-t-l text-bold">
      Total Asset Weighting:{" "}
        {_.reduce(
          tierBasedTags[selectedTier],
          (acc, curr) => acc + curr.value,
          0
        )}
    </div>)
    
  return (
    <div style={{ width: 590 }}>
      <Select
        options={TIER_OPTIONS}
        style={{ width: 100 }}
        value={selectedTier}
        onChange={setSelectedTier}
      />
      <TableContainer
        columns={columns}
        dataSource={tierBasedTags?.[selectedTier] ?? []}
        rowKey={getTagKey}
        title={title}
      />
        <AuthorisedContainer permission={PermissionSetMap.UPDATE_GLOBAL_SETTING} tooltip="Add Tag">
          <Button 
            className="pull-left" 
            type="link" 
            size="small" 
            onClick={addTagsClick} 
            icon={<PlusOutlined />}>
            Add Tags
          </Button>
        </AuthorisedContainer>
    </div>
  );
})

export default TierBasedTags;


export const AddTagsToTierPopup = forwardRef(function AddTagsToTierPopup(props, ref) {
  const [form] = Form.useForm();
  const { tier, tierBasedTags, allTags, saveTags, tags } = props;
  const onSubmit = async (values) => {
    const allTagsMap = getTagKeyMap(allTags);
    const selectedTags = _.map(values.tags, tagString => {
      return allTagsMap[tagString];
    });
    await saveTags(selectedTags);
  };

  const getOptions = useCallback(() =>  _.map(allTags, (tagObj) => {
    const tagData = form.getFieldsValue().tags
    const selectedTags = tagData || _.map(tags, i => getTagKey(i));
    const values = tagData ? _.map(form.getFieldsValue().tags, i => i.split(":")[0]) : _.map(tags, i => i.name);
    const key = getTagKey(tagObj);
    return {
      label: `${key} (${tagObj.value})`,
      value: key,
      disabled: !selectedTags.includes(key) && values.includes(tagObj.name)
    };
  }), [allTags, form, tags]);

  const field = useMemo(() => [{
    name: "tags",
    label: "Tags",
    type: FieldTypes.MULTI_SELECT,
    props: {
      optionsMutator: getOptions
    }
  }], [getOptions]);

  return (
    <BaseModalForm
      modalProps={{
        okText: "Save",
      }}
      onOk={onSubmit}
      ref={ref}
      title={`Add tags to ${_.find(TIER_OPTIONS_WITH_CUSTOM, { value: tier })?.label || _.find(tierBasedTags, { id: tier })?.name}`}
      fields={field}
      form={form}
      afterClose={props.afterClose}
      initialValues={{
        tags: _.map(tags, getTagKey)
      }}
    />
  );
})